import type { VoyageEvent, VoyageSummary } from '@felix/domain';
import { ActionIcon, Button } from '@mantine/core';
import {
  IconArrowsMaximize,
  IconArrowsMinimize,
  IconTimeline,
  type TablerIcon,
} from '@tabler/icons-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { formatDateTime } from '../../format.js';
import { Panel } from '../../Panel.js';
import { useNumber } from './numbers.js';
import { formatDuration } from './overview.js';
import classes from './VoyageTimeline.module.css';
import {
  TIMELINE_FILTERS,
  type TimelineFilter,
  type TimelineLabel,
  type TimelineMarker,
  timelineModel,
} from './voyage-record.js';

/** "Fit View" kapalıyken gün başına genişlik (px); çizelge kendi içinde yana kayar. */
const DAY_PX = 140;

export interface VoyageTimelineProps {
  summary: VoyageSummary;
  events: readonly VoyageEvent[];
  reports: TimelineInputReports;
  /** Süren seferde planlanan varış; tamamlanan seferde `null`. */
  eta: { at: string; port: string } | null;
  /** Kartın başlığının yanında (All Reports: sefer no). */
  title?: string;
  icon?: TablerIcon;
}

type TimelineInputReports = Parameters<typeof timelineModel>[0]['reports'];

/**
 * Yatay sefer zaman çizelgesi (ADR 0082 Ek §3; ortağın `sefer-6.jpeg` alt bölümü): üstte olaylar,
 * ortada dönem şeridi (seyir, liman, demir, sürüklenme, bekleme, planlanan), altta bacaklar ve
 * kalış süreleri. Süzgeçler: All Events, Reports, Port Operations, Anchorage / Drift, Voyage Legs.
 * "Fit View" bütün seferi karta sığdırır; kapalıyken gün başına sabit genişlik ve iç kaydırma.
 * Tam ekran kartı pencereyi kaplatır (Escape kapatır). Voyage Timeline sayfası ve All Reports
 * kartı aynı bileşeni kullanır.
 */
export function VoyageTimeline({
  summary,
  events,
  reports,
  eta,
  title,
  icon,
}: VoyageTimelineProps) {
  const { t } = useTranslation();
  const n = useNumber();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`voyage.record.timeline.${key}`, options);
  const [filter, setFilter] = useState<TimelineFilter>('all');
  const [fit, setFit] = useState(true);
  const [full, setFull] = useState(false);
  // Görünen şerit genişliği (px): yakın noktaların birleşmesi ve etiket aralığı buna göre
  const scroller = useRef<HTMLElement | null>(null);
  const [visible, setVisible] = useState(1000);
  useEffect(() => {
    const element = scroller.current;
    if (!element || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      // Şerit: kutunun içi eksi çizelgenin iki yan boşluğu (2 × 64 px), en az 720 − 128
      setVisible(Math.max(592, element.clientWidth - 8 - 128));
    });
    observer.observe(element);
    return () => observer.disconnect();
  });
  useEffect(() => {
    if (!full) return;
    const close = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setFull(false);
    };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [full]);

  const days = useMemo(() => {
    const start = summary.start ? Date.parse(summary.start) : 0;
    const end = Math.max(
      summary.end ? Date.parse(summary.end) : start,
      eta ? Date.parse(eta.at) : 0,
    );
    return Math.max(1, (end - start) / 86_400_000);
  }, [summary, eta]);
  // Sığdırılmamış çizelge: gün başına sabit genişlik, en az görünen genişlik
  const widthPx = fit ? visible : Math.max(visible, days * DAY_PX);
  const model = useMemo(
    () => timelineModel({ summary, events, reports, eta, filter, widthPx }),
    [summary, events, reports, eta, filter, widthPx],
  );

  const markerName = (marker: { name: string; type?: string }) =>
    marker.type === 'report'
      ? t(`voyage.kinds.${marker.name}`)
      : marker.name === 'eta'
        ? k('eta', { port: eta?.port ?? '' })
        : t(`voyage.record.eventTypes.${marker.name}`);
  const markerTitle = (marker: TimelineMarker) =>
    [marker, ...marker.merged.map((m) => ({ ...m, type: marker.type }))]
      .map((m) => `${markerName(m)} · ${formatDateTime(m.at, 0)}`)
      .join('\n');
  const labelText = (label: TimelineLabel) =>
    label.kind === 'leg'
      ? k('leg', { no: label.no, distance: n.unit(label.distanceNm, 'nm', 0) })
      : label.kind === 'planned'
        ? k('planned', { duration: formatDuration(label.hours) })
        : k('stay', {
            name: t(`voyage.record.stays.${label.state}`),
            duration: formatDuration(label.hours),
          });

  const toolbar = (
    <div className={classes.toolbar}>
      {/* biome-ignore lint/a11y/useSemanticElements: düğme grubu (aria-pressed) */}
      <div className={classes.filters} role="group" aria-label={k('filterLabel')}>
        {TIMELINE_FILTERS.map((value) => (
          <Button
            key={value}
            size="xs"
            variant={filter === value ? 'filled' : 'default'}
            aria-pressed={filter === value}
            onClick={() => setFilter(value)}
          >
            {k(`filters.${value}`)}
          </Button>
        ))}
      </div>
      <div className={classes.view}>
        <Button
          size="xs"
          variant={fit ? 'light' : 'default'}
          aria-pressed={fit}
          onClick={() => setFit((value) => !value)}
        >
          {k('fit')}
        </Button>
        <ActionIcon
          variant="default"
          size="md"
          aria-label={full ? k('exitFullscreen') : k('fullscreen')}
          title={full ? k('exitFullscreen') : k('fullscreen')}
          onClick={() => setFull((value) => !value)}
        >
          {full ? <IconArrowsMinimize size={16} /> : <IconArrowsMaximize size={16} />}
        </ActionIcon>
      </div>
    </div>
  );

  const Icon = icon ?? IconTimeline;
  return (
    <div className={classes.frame} data-full={full || undefined}>
      <Panel
        title={title ?? k('card')}
        icon={<Icon size={20} stroke={1.8} />}
        extra={toolbar}
        className={classes.panel}
      >
        {model ? (
          <section
            ref={scroller}
            className={classes.scroller}
            aria-label={k('region')}
            // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
            tabIndex={0}
          >
            <div
              className={classes.chart}
              style={fit ? undefined : { width: `${widthPx + 128}px` }}
              data-filter={filter}
            >
              <ol className={classes.markers}>
                {model.markers.map((marker) => (
                  <li
                    key={marker.key}
                    className={classes.marker}
                    data-row={marker.row}
                    data-category={marker.category}
                    data-edge={marker.edge ?? undefined}
                    data-labelled={marker.labelled || undefined}
                    style={{ left: `${marker.pct}%` }}
                    title={markerTitle(marker)}
                  >
                    <span className={classes.markerText}>
                      <strong>
                        {markerName(marker)}
                        {marker.merged.length > 0 ? (
                          <span className={classes.more}>
                            {' '}
                            {k('more', { count: marker.merged.length })}
                          </span>
                        ) : null}
                      </strong>
                      <span>
                        {formatDateTime(marker.at, 0)}
                        {marker.type === 'eta' ? ` ${k('est')}` : ''}
                      </span>
                    </span>
                    <span className={classes.stem} aria-hidden />
                    <span className={classes.dot} aria-hidden />
                  </li>
                ))}
              </ol>
              <div className={classes.track} aria-hidden>
                {model.spans.map((span) => (
                  <span
                    key={span.key}
                    className={classes.span}
                    data-state={span.state}
                    style={{ left: `${span.startPct}%`, width: `${span.widthPct}%` }}
                  />
                ))}
              </div>
              <ul className={classes.labels}>
                {model.labels.map((label) => (
                  <li
                    key={label.key}
                    className={classes.label}
                    data-kind={label.kind}
                    data-state={label.state}
                    data-narrow={label.widthPct < 9 || undefined}
                    style={{ left: `${label.startPct}%`, width: `${label.widthPct}%` }}
                    title={labelText(label)}
                  >
                    <span>{labelText(label)}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        ) : (
          <EmptyState title={k('emptyTitle')} description={k('empty')} />
        )}
      </Panel>
    </div>
  );
}
